// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — CasinoShell Context
// State management wrapper for the entire casino ecosystem
// ================================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';

const CasinoContext = createContext({});
export const useCasino = () => useContext(CasinoContext);

// Session limits defaults
const DEFAULT_LIMITS = {
  depositDaily: null,    // null = no limit set
  depositWeekly: null,
  depositMonthly: null,
  lossDaily: null,
  lossWeekly: null,
  lossSession: null,
  timeSession: null,     // minutes
  realityCheck: 30,      // minutes — pop-up interval
};

// Responsible gaming cooloff durations
const COOLOFF_OPTIONS = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '6mo': 180 * 24 * 60 * 60 * 1000,
  '1yr': 365 * 24 * 60 * 60 * 1000,
  'permanent': Infinity,
};

export function CasinoProvider({ children }) {
  const { user } = useAuth();
  const storageKey = `forgecasino_${user?.email || 'anon'}`;

  // ── Core State ──
  const [forgeBalance, setForgeBalance] = useState(0);
  const [activeGame, setActiveGame] = useState(null);
  const [sessionStart, setSessionStart] = useState(null);
  const [kycStatus, setKycStatus] = useState('pending'); // pending | verified | rejected
  const [fairnessLog, setFairnessLog] = useState([]);
  const [transactionHistory, setTransactionHistory] = useState([]);

  // ── Responsible Gaming State ──
  const [sessionLimits, setSessionLimits] = useState(DEFAULT_LIMITS);
  const [selfExclusion, setSelfExclusion] = useState(null); // null or { until: timestamp, type: string }
  const [sessionStats, setSessionStats] = useState({
    totalBets: 0,
    totalWins: 0,
    totalLosses: 0,
    netPosition: 0,
    spinsThisSession: 0,
    depositsToday: 0,
    depositsThisWeek: 0,
    depositsThisMonth: 0,
    lossesToday: 0,
    lossesThisWeek: 0,
  });
  const [realityCheckDue, setRealityCheckDue] = useState(false);
  const [lastRealityCheck, setLastRealityCheck] = useState(null);

  // ── Load saved state ──
  useEffect(() => {
    if (!user) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const data = JSON.parse(saved);
        if (data.forgeBalance != null) setForgeBalance(data.forgeBalance);
        if (data.kycStatus) setKycStatus(data.kycStatus);
        if (data.sessionLimits) setSessionLimits({ ...DEFAULT_LIMITS, ...data.sessionLimits });
        if (data.selfExclusion) setSelfExclusion(data.selfExclusion);
        if (data.transactionHistory) setTransactionHistory(data.transactionHistory.slice(-500));
        if (data.fairnessLog) setFairnessLog(data.fairnessLog.slice(-100));
      }
    } catch (e) { /* silent */ }
    setSessionStart(Date.now());
    setLastRealityCheck(Date.now());
  }, [user, storageKey]);

  // ── Persist state ──
  useEffect(() => {
    if (!user) return;
    const data = {
      forgeBalance,
      kycStatus,
      sessionLimits,
      selfExclusion,
      transactionHistory: transactionHistory.slice(-500),
      fairnessLog: fairnessLog.slice(-100),
    };
    localStorage.setItem(storageKey, JSON.stringify(data));
  }, [forgeBalance, kycStatus, sessionLimits, selfExclusion, transactionHistory, fairnessLog, user, storageKey]);

  // ── Reality Check Timer ──
  useEffect(() => {
    if (!sessionStart || !sessionLimits.realityCheck) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - (lastRealityCheck || sessionStart);
      if (elapsed >= sessionLimits.realityCheck * 60 * 1000) {
        setRealityCheckDue(true);
      }
    }, 10000); // check every 10s
    return () => clearInterval(interval);
  }, [sessionStart, sessionLimits.realityCheck, lastRealityCheck]);

  // ── Self-exclusion check ──
  const isExcluded = useCallback(() => {
    if (!selfExclusion) return false;
    if (selfExclusion.type === 'permanent') return true;
    return Date.now() < selfExclusion.until;
  }, [selfExclusion]);

  // ── Deposit with limit checking ──
  const deposit = useCallback((amount) => {
    if (isExcluded()) return { success: false, reason: 'Self-exclusion active' };
    if (amount <= 0) return { success: false, reason: 'Invalid amount' };

    // Check daily deposit limit
    if (sessionLimits.depositDaily && sessionStats.depositsToday + amount > sessionLimits.depositDaily) {
      return { success: false, reason: `Daily deposit limit ($${sessionLimits.depositDaily}) would be exceeded` };
    }
    if (sessionLimits.depositWeekly && sessionStats.depositsThisWeek + amount > sessionLimits.depositWeekly) {
      return { success: false, reason: `Weekly deposit limit ($${sessionLimits.depositWeekly}) would be exceeded` };
    }
    if (sessionLimits.depositMonthly && sessionStats.depositsThisMonth + amount > sessionLimits.depositMonthly) {
      return { success: false, reason: `Monthly deposit limit ($${sessionLimits.depositMonthly}) would be exceeded` };
    }

    setForgeBalance(prev => prev + amount);
    setSessionStats(prev => ({
      ...prev,
      depositsToday: prev.depositsToday + amount,
      depositsThisWeek: prev.depositsThisWeek + amount,
      depositsThisMonth: prev.depositsThisMonth + amount,
    }));
    addTransaction('deposit', amount, 'Deposit to casino wallet');
    return { success: true };
  }, [isExcluded, sessionLimits, sessionStats]);

  // ── Withdraw ──
  const withdraw = useCallback((amount) => {
    if (kycStatus !== 'verified') return { success: false, reason: 'KYC verification required for withdrawals' };
    if (amount <= 0 || amount > forgeBalance) return { success: false, reason: 'Insufficient balance' };

    setForgeBalance(prev => prev - amount);
    addTransaction('withdraw', amount, 'Withdrawal from casino wallet');
    return { success: true };
  }, [kycStatus, forgeBalance]);

  // ── Place bet with loss limit checking ──
  const placeBet = useCallback((amount, gameId) => {
    if (isExcluded()) return { success: false, reason: 'Self-exclusion active' };
    if (amount <= 0) return { success: false, reason: 'Invalid bet' };
    if (amount > forgeBalance) return { success: false, reason: 'Insufficient $FORGE balance' };

    // Check loss limits
    if (sessionLimits.lossSession && sessionStats.netPosition - amount < -sessionLimits.lossSession) {
      return { success: false, reason: `Session loss limit ($${sessionLimits.lossSession}) would be exceeded` };
    }
    if (sessionLimits.lossDaily && sessionStats.lossesToday + amount > sessionLimits.lossDaily) {
      return { success: false, reason: `Daily loss limit ($${sessionLimits.lossDaily}) would be exceeded` };
    }

    // Check time limit
    if (sessionLimits.timeSession && sessionStart) {
      const elapsed = (Date.now() - sessionStart) / 60000;
      if (elapsed >= sessionLimits.timeSession) {
        return { success: false, reason: `Session time limit (${sessionLimits.timeSession} minutes) reached` };
      }
    }

    setForgeBalance(prev => prev - amount);
    setSessionStats(prev => ({
      ...prev,
      totalBets: prev.totalBets + amount,
      spinsThisSession: prev.spinsThisSession + 1,
    }));
    return { success: true };
  }, [isExcluded, forgeBalance, sessionLimits, sessionStats, sessionStart]);

  // ── Record win ──
  const recordWin = useCallback((amount, gameId, spinHash) => {
    setForgeBalance(prev => prev + amount);
    setSessionStats(prev => ({
      ...prev,
      totalWins: prev.totalWins + amount,
      netPosition: prev.netPosition + amount,
    }));
    addTransaction('win', amount, `Win on ${gameId}`, spinHash);
  }, []);

  // ── Record loss ──
  const recordLoss = useCallback((amount, gameId, spinHash) => {
    setSessionStats(prev => ({
      ...prev,
      totalLosses: prev.totalLosses + amount,
      lossesToday: prev.lossesToday + amount,
      lossesThisWeek: prev.lossesThisWeek + amount,
      netPosition: prev.netPosition - amount,
    }));
    addTransaction('loss', amount, `Loss on ${gameId}`, spinHash);
  }, []);

  // ── Transaction logging ──
  const addTransaction = (type, amount, description, hash) => {
    const tx = {
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type,
      amount,
      description,
      hash: hash || null,
      timestamp: new Date().toISOString(),
      balance: type === 'deposit' || type === 'win' ? forgeBalance + amount : forgeBalance - amount,
    };
    setTransactionHistory(prev => [...prev, tx]);
  };

  // ── Fairness logging ──
  const logFairness = useCallback((entry) => {
    setFairnessLog(prev => [...prev, {
      ...entry,
      timestamp: new Date().toISOString(),
    }]);
  }, []);

  // ── Self-exclusion ──
  const activateSelfExclusion = useCallback((type) => {
    const duration = COOLOFF_OPTIONS[type];
    if (!duration) return;
    setSelfExclusion({
      type,
      activatedAt: Date.now(),
      until: type === 'permanent' ? Infinity : Date.now() + duration,
    });
  }, []);

  // ── Reality check acknowledgment ──
  const acknowledgeRealityCheck = useCallback(() => {
    setRealityCheckDue(false);
    setLastRealityCheck(Date.now());
  }, []);

  // ── Update limits ──
  const updateLimits = useCallback((newLimits) => {
    setSessionLimits(prev => ({ ...prev, ...newLimits }));
  }, []);

  // ── Session duration ──
  const getSessionDuration = useCallback(() => {
    if (!sessionStart) return 0;
    return Math.floor((Date.now() - sessionStart) / 60000); // minutes
  }, [sessionStart]);

  const value = {
    // Balance
    forgeBalance,
    deposit,
    withdraw,

    // Gaming
    activeGame,
    setActiveGame,
    placeBet,
    recordWin,
    recordLoss,

    // KYC
    kycStatus,
    setKycStatus,

    // Fairness
    fairnessLog,
    logFairness,

    // Transactions
    transactionHistory,

    // Responsible Gaming
    sessionLimits,
    updateLimits,
    sessionStats,
    selfExclusion,
    activateSelfExclusion,
    isExcluded,
    realityCheckDue,
    acknowledgeRealityCheck,
    getSessionDuration,

    // Constants
    COOLOFF_OPTIONS,
  };

  return (
    <CasinoContext.Provider value={value}>
      {children}
    </CasinoContext.Provider>
  );
}
